/**
 * Diagnostic results insights — the shared brain behind the post-assessment
 * results screen on BOTH apps (mobile AssessmentReview, web OnboardingTestReview).
 *
 * The results screen is the guest→signup conversion surface, so every derived
 * fact a user sees there (domain breakdown, status chips, highlights,
 * score-band copy) must be byte-identical across web and mobile. Both apps
 * previously duplicated the per-domain reduce and shipped DIFFERENT
 * score-band copy for the same score; this module is the single source both
 * consume.
 *
 * Color rule: functions return semantic tone KEYS ('error'|'warning'|'info'|
 * 'success'), never hex — apps map tones to @learnzapp/core/theme tokens.
 */

/**
 * Normalize per-question diagnostic metadata into per-domain results.
 *
 * @param {Array}  questionsMeta test_questions_meta_data: [{ topic_id, response, ... }]
 * @param {Array}  appDomains    GET_APP_DOMAIN_LIST output: [{ id, domain_name }]
 * @param {Object} opts          { correctTag } — the app's STRING.correct_tag
 * @returns {Array} [{ domainId, domainName, correct, total, pct }] in appDomains
 *   order; domains with no diagnostic questions are excluded. topic_id/domain id
 *   comparison is String-normalized (matching questionPools' type-insensitive rule).
 */
export function buildDiagnosticDomainResults(questionsMeta, appDomains, { correctTag } = {}) {
  const meta = (questionsMeta ?? []).filter(Boolean);
  const results = [];
  for (const domain of appDomains ?? []) {
    const domainId = domain?.id;
    const inDomain = meta.filter((q) => String(q?.topic_id) === String(domainId));
    if (inDomain.length === 0) continue;
    const correct = inDomain.filter((q) => q?.response === correctTag).length;
    results.push({
      domainId,
      domainName: domain?.domain_name,
      correct,
      total: inDomain.length,
      pct: Math.round((100 * correct) / inDomain.length),
    });
  }
  return results;
}

/**
 * Status chip for one domain's diagnostic result — count-aware, not
 * percentage-only. A diagnostic samples 1–3 questions per domain, so raw
 * percentages are mostly noise: a perfect 1/1 is not evidence of strength,
 * which is why 'Strong' additionally requires total >= 2. The UI must always
 * render "x/y correct" beside the chip so the label stays honest at n=1.
 *
 * @returns {{ key: string, label: string, tone: string }}
 */
export function getDiagnosticDomainStatus({ correct, total } = {}) {
  const c = correct ?? 0;
  const t = total ?? 0;
  const ratio = t > 0 ? c / t : 0;
  if (c === 0) return { key: 'needs_focus', label: 'Needs Focus', tone: 'error' };
  if (ratio <= 0.5) return { key: 'needs_work', label: 'Needs Work', tone: 'warning' };
  if (ratio < 1 || t < 2) return { key: 'keep_improving', label: 'Keep Improving', tone: 'info' };
  return { key: 'strong', label: 'Strong', tone: 'success' };
}

/**
 * "Strongest Domain" / "Biggest Opportunity" hero callouts.
 *
 * strongest: highest correct ratio (tiebreak: more questions, then lower id).
 * opportunity: most missed questions (tiebreak: lower ratio, more questions,
 * lower id) — missed COUNT, not percentage, because the domain where the most
 * questions were missed is where study time buys the most points.
 *
 * @returns {{ strongest: Object|null, opportunity: Object|null }} entries from
 *   domainResults; opportunity is null when it would name the same domain as
 *   strongest (single-domain case) — apps hide that callout.
 */
export function getDomainHighlights(domainResults) {
  const results = (domainResults ?? []).filter((d) => d && d.total > 0);
  if (results.length === 0) return { strongest: null, opportunity: null };

  const ratio = (d) => d.correct / d.total;
  const byId = (a, b) => String(a.domainId).localeCompare(String(b.domainId));

  const strongest = [...results].sort(
    (a, b) => ratio(b) - ratio(a) || b.total - a.total || byId(a, b)
  )[0];
  const opportunity = [...results].sort(
    (a, b) =>
      (b.total - b.correct) - (a.total - a.correct) ||
      ratio(a) - ratio(b) ||
      b.total - a.total ||
      byId(a, b)
  )[0];

  if (opportunity.domainId === strongest.domainId) {
    return { strongest, opportunity: null };
  }
  return { strongest, opportunity };
}

/**
 * Personalized hero sentence naming the user's strongest + focus domains.
 * Returns null when either highlight is missing (single-domain or empty
 * results) — apps fall back to the score-band copy.message.
 *
 * @param {Object} highlights getDomainHighlights() output
 * @returns {string|null}
 */
export function getPersonalizedResultsMessage(highlights) {
  const strongest = highlights?.strongest;
  const opportunity = highlights?.opportunity;
  if (!strongest?.domainName || !opportunity?.domainName) return null;
  return `Your strongest area is ${strongest.domainName}. Focus on ${opportunity.domainName} next to improve your readiness fastest.`;
}

/**
 * Copy for the compact signup card on the results screen. Static, but served
 * from core so a copy experiment updates both platforms together.
 */
export function getSignupCardCopy() {
  return {
    title: 'Save your results — free',
    body: 'Keep your readiness score and pick up where you left off.',
    appleCta: 'Continue with Apple',
    googleCta: 'Continue with Google',
    emailCta: 'Or continue with email',
    trustLine: 'Join 250,000+ professionals preparing with LearnZapp',
  };
}

/** Copy for the exit-intent sheet shown when a guest leaves the results screen. */
export function getExitSheetCopy() {
  return {
    title: "Your results aren't saved",
    body: 'Sign up free to keep your readiness score and pick up where you left off.',
    primaryCta: 'Save my results',
    dismissCta: 'Not now',
  };
}

/**
 * Copy for the closing conversion card at the end of the guest answer review.
 *
 * @param {number} viewedCount    questions the user actually viewed
 * @param {number} totalQuestions cert question-library total (0 → omitted)
 * @param {string} [certName]     cert display name (omitted when absent)
 */
export function getPostReviewCardCopy(viewedCount, totalQuestions, certName) {
  const totalLabel = totalQuestions > 0 ? `${totalQuestions} ` : '';
  const certLabel = certName ? `${certName} ` : '';
  return {
    headline: `That was ${viewedCount} of ${totalLabel}${certLabel}practice questions`,
    subline: 'Sign up free to keep practicing and track your readiness as it climbs.',
    primaryCta: 'Continue free',
    dismissCta: 'Back to results',
  };
}

/**
 * Missed questions on the results screen: anything that didn't earn credit.
 * Unanswered counts as missed, matching the score math (an unanswered
 * question already lowers the score everywhere in the product).
 *
 * @param {Object} counts { incorrect, unanswered }
 */
export function getMissedCount(counts) {
  const { incorrect, unanswered } = counts ?? {};
  return (incorrect ?? 0) + (unanswered ?? 0);
}

/**
 * Copy for the compact review-entry row on the results screen.
 *
 * @param {number} total       questions in the diagnostic
 * @param {number} missedCount incorrect + unanswered, per getMissedCount
 *                             (0 → shorter subtitle)
 */
export function getReviewEntryCopy(total, missedCount) {
  return {
    title: 'Review your answers',
    subtitle: missedCount > 0
      ? `All ${total} questions with full explanations — see the ${missedCount} you missed`
      : `All ${total} questions with full explanations`,
  };
}

/**
 * Muted cert-inventory caption for the results screen, e.g.
 * "356 practice questions · 7 full exams · 104 flashcards · 626 study articles".
 * Zero/absent counts are omitted; returns '' when nothing is available.
 *
 * @param {Object} counts { questions, exams, flashcards, articles }
 */
export function buildInventoryLine({ questions, exams, flashcards, articles } = {}) {
  return [
    { count: questions, label: 'practice questions' },
    { count: exams, label: 'full exams' },
    { count: flashcards, label: 'flashcards' },
    { count: articles, label: 'study articles' },
  ]
    .filter((row) => row.count > 0)
    .map((row) => `${row.count} ${row.label}`)
    .join(' · ');
}

/**
 * Hero gauge tone for the overall score. Deliberately never 'error': a low
 * diagnostic score is the START of the funnel, and a failing-red gauge reads
 * as "this exam isn't for me" — warm/neutral tones only.
 *
 * @returns {'warning'|'info'|'success'}
 */
export function getDiagnosticScoreTone(scorePct) {
  const score = scorePct ?? 0;
  if (score < 50) return 'warning';
  if (score < 80) return 'info';
  return 'success';
}

/**
 * Score-band copy for the results screen. Single source for BOTH apps — they
 * previously shipped different copy for the same score. Bands match the
 * legacy tiers (<=30 / <=60 / <=80 / else). `cta` is the tiered prep label
 * used for already-authenticated users; guests get the fixed
 * "Create Free Account & Continue" CTA in the apps.
 *
 * @returns {{ headline: string, message: string, cta: string }}
 */
export function getDiagnosticResultsCopy(scorePct, examName) {
  const score = scorePct ?? 0;
  const exam = examName || 'your exam';
  // CTA reads "Start Your AIGP Prep" with a name, "Start Your Prep" without.
  const ctaExam = examName ? `${examName} ` : '';
  if (score <= 30) {
    return {
      headline: "You're just getting started.",
      message: `Keep going! Focus on your weakest areas and you'll be ready for ${exam}.`,
      cta: `Start Your ${ctaExam}Prep`,
    };
  }
  if (score <= 60) {
    return {
      headline: "You're on your way.",
      message: `Solid start! Strengthening your weakest areas will get you to ${exam} readiness fastest.`,
      cta: `Continue Your ${ctaExam}Prep`,
    };
  }
  if (score <= 80) {
    return {
      headline: "You're almost there.",
      message: `Great work! A focused review of your weaker areas will make you ${exam}-ready.`,
      cta: `Complete Your ${ctaExam}Prep`,
    };
  }
  return {
    headline: "You're exam-ready.",
    message: `Outstanding! Lock in your ${exam} success with practice tests and a final review.`,
    cta: `Ace Your ${ctaExam}Exam`,
  };
}
